import type { AxiosInstance } from "axios";
import type { FirewallPolicies } from "../policies/policies.types";
import type { FirewallEvents } from "../events/events.types";
import type { FirewallRouting } from "../routing/routing.types";
import type { FirewallNat } from "../nat/nat.types";

/**
 * The detail's technical readings (CTX-1164, CTX-1176, CTX-1194): each its own
 * `GET /kontroledge/fwaas/:id/<reading>` answering `{ <reading>: … | null }`.
 */
export interface FwaasReadings {
    policies: FirewallPolicies;
    events: FirewallEvents;
    routing: FirewallRouting;
    nat: FirewallNat;
}

export type FwaasReadingKey = keyof FwaasReadings;

/** Dev-only: the overview's fixture case also picks every tab's reading. */
const MOCK_CASE = process.env.REACT_APP_MOCK_FWAAS_OVERVIEW;

async function fixtureFor<K extends FwaasReadingKey>(key: K): Promise<FwaasReadings[K] | null | undefined> {
    const [{ isOverviewFixtureCase }, { READING_FIXTURES }] = await Promise.all([
        import("../overview/overviewFixtures"),
        import("./readingFixtures"),
    ]);
    if (!MOCK_CASE || !isOverviewFixtureCase(MOCK_CASE)) return undefined;
    return READING_FIXTURES[key][MOCK_CASE];
}

/**
 * One tab's reading, unwrapped. `null` when the firewall could not answer — the request
 * itself succeeded. Throws when the envelope is not what we expect.
 */
export async function fetchFwaasReading<K extends FwaasReadingKey>(
    api: AxiosInstance,
    tenantId: number,
    fwaasId: string,
    key: K
): Promise<FwaasReadings[K] | null> {
    if (MOCK_CASE) {
        const fixture = await fixtureFor(key);
        if (fixture !== undefined) return fixture;
    }
    const response = await api.get(`/kontroledge/fwaas/${encodeURIComponent(fwaasId)}/${key}`, {
        params: { tenantId },
    });
    const body = response.data as {
        success?: boolean;
        message?: string;
        data?: Partial<Record<K, FwaasReadings[K] | null>>;
    };
    if (!body?.success || !body.data) {
        throw new Error(body?.message ?? `Failed to load the Virtual Firewall ${key}`);
    }
    return body.data[key] ?? null;
}
